<template>
  <div>
    <div class="columns is-desktop gutter">
      <div
        class="column is-one-fifth-fullhd is-one-fifth-widescreen is-one-quarter-desktop"
      >
        <div class="has-text-right is-hidden-desktop">
          <button
            class="button"
            aria-controls="track-search-filters"
            :aria-expanded="showFilters ? 'true' : 'false'"
            @click="showFilters = !showFilters"
          >
            <span>Filter</span>
            <span class="icon-static small chevron-down" />
          </button>
        </div>
        <div
          id="track-search-filters"
          class="filter-bar filter-panel"
          :class="{ 'is-open': showFilters }"
        >
          <bulma-accordion>
            <bulma-accordion-item
              v-for="(searchOption, i) in multiselectSearchOptions"
              :key="searchOption.field + i"
              class="field"
            >
              <h4
                slot="title"
                class="title is-4"
              >
                {{ searchOption.label }}
              </h4>
              <div slot="content">
                <div class="control">
                  <div class="is-fullwidth">
                    <div
                      v-if="searchOption.field === 'tempo'"
                      class="tempo"
                    >
                      <b-field>
                        <b-slider
                          v-model="tempoRange"
                          :min="0"
                          :max="2"
                          :step="1"
                          :tooltip="false"
                          lazy
                          @change="handleTempoSliderChange"
                        >
                          <b-slider-tick :value="0">
                            Slow
                          </b-slider-tick>
                          <b-slider-tick :value="1">
                            Medium
                          </b-slider-tick>
                          <b-slider-tick :value="2">
                            Fast
                          </b-slider-tick>
                        </b-slider>
                      </b-field>
                    </div>
                    <div v-else>
                      <ol>
                        <li
                          v-for="optionValue in searchOption.values"
                          :key="optionValue.pk"
                          class="text medium"
                          :class="{ selected: isOptionValueSelected(searchOption, optionValue) }"
                          @click="handleMultiOptionClick(searchOption, optionValue)"
                        >
                          <span>{{ optionValue.name }}</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </bulma-accordion-item>
          </bulma-accordion>
          <div class="switches">
            <div
              v-for="option in booleanSearchOptions"
              :key="option.field"
              class="field-switch"
            >
              <b-switch
                size="is-small"
                :value="isBooleanOptionSelected(option)"
                @click.native.prevent="handleBooleanOptionClick(option)"
              />
              <h5
                slot="title"
                class="title is-5"
              >
                {{ option.label }}
              </h5>
            </div>
          </div>
          <button
            class="button is-primary is-large is-fullwidth is-hidden-desktop mt-4"
            @click="showFilters = false"
          >
            Done
          </button>
        </div>
      </div>

      <div class="column">
        <b-notification
          v-if="isFeatureEnabled('sfx_upgrade_cta_tracks_page') && showSfxUpgradeCta"
          type="is-warning is-light"
        >
          It looks like you're on a legacy plan. <router-link :to="{ name: 'subscription' }">Click here to upgrade and access Sfx!</router-link>
        </b-notification>
        <div class="field">
          <div class="control">
            <vue-autosuggest
              ref="autocomplete"
              v-model="searchText"
              :input-props="autosuggestInputProps"
              :section-configs="autosuggestSectionConfigs"
              :suggestions="filteredAutosuggestSearchOptions"
              :get-suggestion-value="getSuggestionValue"
              :should-render-suggestions="(size, loading) => size > 0 && !loading && searchText !== ''"
              @selected="handleAutosuggestItemSelected"
              @keydown="handleAutosuggestKeypress"
            >
              <template
                slot="before-section-default"
                slot-scope="{section}"
              >
                <li>
                  <h4 class="title is-4 autosuggest__section-title">
                    {{ section.label }}
                  </h4>
                </li>
              </template>
              <template slot-scope="{suggestion}">
                <span v-if="suggestion.item.searchFieldName === 'artists'">
                  <img
                    class="image is-32x32"
                    style="float: left;"
                    :src="suggestion.item.object.image_avatar"
                  >
                  <span class="item-text">{{ suggestion.item.fieldOption }}</span>
                </span>
                <span v-else-if="suggestion.item.searchFieldName === 'playlists'">
                  <img
                    class="image is-32x32"
                    style="float: left;"
                    :src="suggestion.item.object.image_search"
                  >
                  <span class="item-text">{{ suggestion.item.fieldOption }}</span>
                </span>
                <span
                  v-else
                  :class="{ selected: isOptionValueSelected(suggestion.item.searchFieldName, suggestion.item.fieldOption) }"
                  class="item-text"
                >
                  {{ suggestion.item.fieldOption }}
                </span>
              </template>
            </vue-autosuggest>
          </div>
        </div>
        <p class="ai-search-nudge">
          Know the feeling but not the keywords?
          <router-link :to="{ name: 'ai-track-search' }">
            <sparkle-icon />Describe it with AI search
          </router-link>
        </p>
        <div
          v-if="searchTags.length > 0"
          class="tags"
        >
          <span
            v-for="tag in searchTags"
            :key="tag.field + tag.value"
            class="tag is-rounded is-clickable tag-accent-fade"
            @click="removeSearchSelection(tag.field, tag.type, tag.value)"
          >
            <span style="font-weight: 500;">{{ tag.label }}</span>
            <button
              class="delete is-small"
              @click="removeSearchSelection(tag.field, tag.type, tag.value)"
            />
          </span>
          <span
            class="tag is-rounded clear-all is-clickable"
            @click="clearSearch"
          >
            <span style="font-weight: 500;">Clear all</span>
          </span>
        </div>
        <div
          v-if="results.length > 0 && !loadingNewSearch"
          class="is-fullwidth"
        >
          <track-list-item
            v-for="track in results"
            :key="track.pk"
            :track="track"
          />
        </div>
        <div
          v-if="results.length === 0 && !loadingNewSearch"
          class="is-fullwidth"
        >
          <div class="container">
            <div class="columns is-vcentered mt-6">
              <div class="column is-6 is-offset-3 has-text-centered">
                <h3 class="title-wide is-1 text mb-5">
                  Looking for something specific?
                </h3>
                <p class="text large mb-4">
                  We couldn't find anything for your search. You have added <span style="font-weight: 500;">{{ searchTags.length }}</span> search filters.
                  Try reducing the number of filters you've added or
                  <a
                    style="font-weight: 500;"
                    @click.prevent="clearSearch"
                  >clear all</a>
                  to start afresh.
                </p>
                <p class="text large">
                  Need help finding the perfect track? Contact us via live chat and we'll help you out.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="loading"
          class="columns"
        >
          <div class="column is-12 has-text-centered">
            <br>
            <span
              class="loader xl"
              style="display: inline-block; width: 3em; height: 3em;"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- div.columns -->
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { VueAutosuggest } from 'vue-autosuggest'
import { BulmaAccordion, BulmaAccordionItem } from 'vue-bulma-accordion'
import TrackListItem from '@/components/TrackListItem.vue'
import SparkleIcon from '@/components/SparkleIcon.vue'
import searchClient from '@/api/trackSearch.js'
import { formatComponentName } from '@/utils/common.js'
import { convertSearchArtistToCatalogueArtist, convertSearchPlaylistToCataloguePlaylist } from '@/utils/catalogue.js'
import { logSearchParameterSet, logClearSearch, logSearchBarArtistSelected } from '@/utils/segment.js'
import { currentSearchSession } from '@/utils/searchAnalytics.js'
import { pageArrival } from '@/utils/pageArrival.js'

const MIN_SEARCH_TERM_LENGTH = 2

export default {
  name: 'TrackSearch',

  components: {
    TrackListItem,
    SparkleIcon,
    BulmaAccordion,
    BulmaAccordionItem,
    VueAutosuggest
  },

  data () {
    return {
      // Guards the cleanSearchParams watcher during init+hydrate so reset and
      // hydrate mutations don't race two doSearch calls against each other.
      suppressAutoSearch: true,
      // Search analytics: where the next new search comes from (page_load,
      // link, reload, filter_click or search_box).
      searchSource: 'page_load',
      // Below desktop width the filter panel is collapsed behind the "Filter" button.
      showFilters: false,
      unusedTempoRange: [], // This is a dummy value to keep Buefy tempo slider happy.
      searchText: '',
      autosuggestInputProps: {
        id: 'autosuggest__input',
        placeholder: 'artists, genres, moods, themes and instruments',
        class: 'input is-large search'
      },
      autosuggestSectionConfigs: {
        default: {
          limit: 6
        }
      },
      toggleDummyValues: [],
      artistSearchResults: [],
      playlistSearchResults: [],
      timeout: null,
      debounceMilliseconds: 250
    }
  },

  computed: {
    ...mapGetters({ isFeatureEnabled: 'waffle/isFeatureEnabled' }),
    ...mapGetters('trackSearch', [
      'cleanSearchParams',
      'searchTags',
      'multiselectSearchOptions',
      'booleanSearchOptions',
      'isOptionValueSelected',
      'isBooleanOptionSelected',
      'loading',
      'loadingNewSearch'
    ]),
    ...mapState('trackSearch', ['results']),

    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    componentName: function () {
      return formatComponentName(this)
    },

    showSfxUpgradeCta: function () {
      return this.$store.getters['subscriptions/upgradeablePlans'].length > 0
    },

    tempoRange: {
      get () {
        return this.$store.state.trackSearch.searchParams.tempo
      },
      set (arr) {
        // We don't use this value. As Buefy is calling this setter twice we bind the
        // slider to an unused variable then handle changes to the Vuex state manually
        // with the sliders @change event. See `handleTempoSliderChange`.
        this.unusedTempoRange = arr
      }
    },

    searchFields: function () {
      return [...new Set(this.multiselectSearchOptions.map(o => {
        return o.label
      }))]
    },

    flatSearchOptions: function () {
      return this.multiselectSearchOptions.map(o => {
        return o.values.map(v => {
          return {
            searchFieldLabel: o.label,
            searchFieldName: o.field,
            fieldOption: v.name,
            searchOption: o,
            optionValue: v
          }
        })
      }).reduce((acc, cur) => [...acc, ...cur], [])
    },

    autosuggestSearchOptions: function () {
      return this.searchFields.map(f => ({
        label: f,
        name: f,
        data: this.flatSearchOptions.filter(o => o.searchFieldLabel === f)
      }))
    },

    filteredAutosuggestSearchOptions: function () {
      const vm = this
      const filtered = []
      const lowerSearchTerm = this.searchText.toLowerCase().trim()
      if (lowerSearchTerm.length < MIN_SEARCH_TERM_LENGTH) {
        return []
      }

      // Artists
      const artistValues = []
      const artistField = 'artists'

      this.artistSearchResults.forEach(artist => {
        artistValues.push({
          searchFieldLabel: 'Artists',
          searchFieldName: artistField,
          fieldOption: artist.name,
          object: convertSearchArtistToCatalogueArtist({ ...artist })
        })
      })

      artistValues.length > 0 &&
        filtered.push({
          label: artistField,
          data: artistValues
        })

      // Playlists
      const playlistValues = []
      const playlistField = 'playlists'

      this.playlistSearchResults.forEach(playlist => {
        playlistValues.push({
          searchFieldLabel: 'Playlists',
          searchFieldName: playlistField,
          fieldOption: playlist.name,
          object: convertSearchPlaylistToCataloguePlaylist({ ...playlist })
        })
      })

      playlistValues.length > 0 &&
        filtered.push({
          label: playlistField,
          data: playlistValues
        })

      // Search options
      vm.searchFields.forEach(field => {
        const optionsForField = vm.flatSearchOptions.filter(o => o.searchFieldLabel === field)
        const values = optionsForField.filter(v => {
          const lowerFieldOption = v.fieldOption.toLowerCase()
          // Only match if the beginning of a word starts with our term (as opposed to
          // finding it in the middle of a word).
          return lowerFieldOption.split(/[,\s-]/).filter(p => p.startsWith(lowerSearchTerm)).length > 0
        })
        values.length > 0 &&
          filtered.push({
            label: field,
            data: values
          })
      })

      return Object.freeze(filtered)
    }
  },

  watch: {
    cleanSearchParams: function () {
      if (this.suppressAutoSearch) return
      this.$nextTick(() => {
        this.newSearch()
      })
    }
  },

  created () {
    window.addEventListener('scroll', this.handleScroll)
  },

  mounted () {
    this.$store.dispatch('waffle/fetch').then(() => {
      this.$store.dispatch('trackSearch/initSearchParams').then(() => {
        this.hydrateSearchParamsFromRoute()
        this.$nextTick(() => {
          this.suppressAutoSearch = false
          this.newSearch()
        })
      })

      if (this.isFeatureEnabled('sfx_plan_upgrade') && this.isFeatureEnabled('sfx_upgrade_cta_tracks_page')) {
        if (this.isLoggedIn) {
          this.$store.dispatch('subscriptions/fetchUpgradeOptions')
        }
      }
    })
  },

  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    hydrateSearchParamsFromRoute: function () {
      // URL grammar:
      //   multiselect: ?<field>=<slug1>,<slug2>  (matched against slug OR name, case-insensitive)
      //   boolean:     ?<field>=true|false       (only takes effect when it matches option.value)
      // Tempo is skipped — its store shape is integer slider positions, not value objects.
      const query = this.$route.query
      if (!query || Object.keys(query).length === 0) return
      const options = this.$store.state.trackSearch.searchOptions
      if (!options || options.length === 0) return
      // Opened with filters in the URL (a landing page, a shared link), unless
      // it's the same address shown again by Reload, Back or Forward.
      if (options.some(option => query[option.field] !== undefined && query[option.field] !== '')) {
        this.searchSource = pageArrival() === 'reload' ? 'reload' : 'link'
      }

      options.forEach(option => {
        const raw = query[option.field]
        if (raw === undefined || raw === '') return
        if (option.field === 'tempo') return

        if (option.type === 'multiselect') {
          const requested = String(raw)
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(Boolean)
          if (requested.length === 0) return
          const matches = option.values.filter(v => {
            const slug = (v.slug || '').toLowerCase()
            const name = (v.name || '').toLowerCase()
            return requested.includes(slug) || requested.includes(name)
          })
          matches.forEach(toggledValue => {
            this.$store.dispatch('trackSearch/handleMultiOptionToggle', {
              searchOption: option,
              toggledValue
            })
          })
        } else if (option.type === 'boolean') {
          if (String(raw).toLowerCase() === String(option.value).toLowerCase()) {
            this.$store.dispatch('trackSearch/handleBooleanOptionToggle', option)
          }
        }
      })
    },

    newSearch: function () {
      this.$store.dispatch('trackSearch/setNextPageUrl', null)
      this.$store.dispatch('trackSearch/setLoadingNewSearch', true)
      this.doSearch({ source: this.searchSource, session: currentSearchSession() })
      // Later new searches come from the filters, unless a search box pick
      // says otherwise.
      this.searchSource = 'filter_click'
    },

    // `analytics` goes with new searches only; a later page's next link
    // already carries it.
    doSearch: function (analytics) {
      this.$store.dispatch('trackSearch/setLoading', true)
      this.$store.dispatch('trackSearch/doSearch', analytics)
        .then((isCurrent) => {
          // A newer search is still loading, so leave its loading state alone.
          if (isCurrent === false) {
            return
          }
          this.$store.dispatch('trackSearch/setLoadingNewSearch', false)
          this.$store.dispatch('trackSearch/setLoading', false)
        })
        .catch((error) => {
          this.$store.dispatch('trackSearch/setLoadingNewSearch', false)
          this.$store.dispatch('trackSearch/setLoading', false)
          this.$store.dispatch('common/showToastMessage', {
            message: 'Something went wrong loading tracks. Please try again.',
            position: 'top-center',
            type: 'is-danger',
            duration: 6000,
            dismissible: true
          })
          return Promise.reject(error)
        })
    },

    clearSearch: function () {
      this.$store.dispatch('trackSearch/resetSearchParams')
      logClearSearch()
    },

    handleAddTextSearch: function (event) {
      event.preventDefault()
      this.$store.dispatch('trackSearch/appendSearchParam', {
        field: 'text',
        value: event.target.value
      })
      event.target.value = ''
    },

    removeSearchSelection: function (field, type, value) {
      if (field === 'tempo') {
        this.$store.dispatch('trackSearch/setSearchParam', {
          field: 'tempo',
          value: []
        })
      } else if (type === 'boolean') {
        this.$store.dispatch('trackSearch/setSearchParam', {
          field,
          value: null
        })
      } else {
        this.$store.dispatch('trackSearch/removeValueFromParams', {
          field,
          value
        })
      }
    },

    handleTempoSliderChange: function (arr) {
      // Change state via handler rather than the computed setter function as Buefy
      // seems to be firing the event twice with the v-model bind.
      this.$store.dispatch('trackSearch/setSearchParam', {
        field: 'tempo',
        value: [...arr] // Copy array to avoid reference and mutating vuex store later in function.
      })
      // Log tempo selection in analytics.
      const arrJson = JSON.stringify(arr.sort())
      let tempoRangeDisplay = null
      if (arrJson === JSON.stringify([0, 2])) {
        tempoRangeDisplay = 'Any'
      } else if (arrJson === JSON.stringify([0, 1])) {
        tempoRangeDisplay = 'Slow-Medium'
      } else if (arrJson === JSON.stringify([1, 2])) {
        tempoRangeDisplay = 'Medium-Fast'
      } else if (arrJson === JSON.stringify([0, 0])) {
        tempoRangeDisplay = 'Slow'
      } else if (arrJson === JSON.stringify([1, 1])) {
        tempoRangeDisplay = 'Medium'
      } else if (arrJson === JSON.stringify([2, 2])) {
        tempoRangeDisplay = 'Fast'
      }
      if (tempoRangeDisplay) {
        logSearchParameterSet(`tempo:${tempoRangeDisplay}`)
      }
    },

    handleMultiOptionClick: function (searchOption, toggledValue) {
      this.$store.dispatch('trackSearch/handleMultiOptionToggle', { searchOption, toggledValue })
        .then((result) => {
          if (result === true) {
            const valueField = searchOption.filter_field // E.g. `name` or `pk`
            const searchValue = toggledValue[valueField] // E.g. "Bass" or `123`
            logSearchParameterSet(`${searchOption.field}:${searchValue}`)
          }
        })
    },

    handleBooleanOptionClick: function (searchOption) {
      this.$store.dispatch('trackSearch/handleBooleanOptionToggle', searchOption)
        .then((result) => {
          if (result === true) {
            logSearchParameterSet(`${searchOption.field}:${searchOption.value}`)
          }
        })
    },

    handleScroll: function (event) {
      // Infinite scroll search results.
      // Don't bother calculating if we're at the bottom if there isn't a next page.
      if (this.$store.state.trackSearch.nextPageUrl !== null) {
        const bottomOfWindow = (
          Math.round(document.documentElement.scrollTop) + window.innerHeight >=
          document.documentElement.offsetHeight
        )
        if (bottomOfWindow && !this.loading) {
          this.doSearch()
        }
      }
    },

    handleAutosuggestKeypress: function (e) {
      this.fetchSearchBoxAPIResults()
    },

    handleAutosuggestItemSelected: function (item) {
      if (!item) {
        return
      }
      if (item.item.searchFieldName === 'artists') {
        // Navigate to URL.
        const artist = item.item.object
        logSearchBarArtistSelected(artist.name)
        this.logSearchBoxSelection({ selection_type: 'artist', artist: artist.pk })
        this.$router.push(this.artistRouteTo(artist))
      } else if (item.item.searchFieldName === 'playlists') {
        // Navigate to URL.
        const playlist = item.item.object
        this.logSearchBoxSelection({ selection_type: 'playlist', playlist: playlist.pk })
        this.$router.push(this.playlistRouteTo(playlist))
      } else {
        const { searchOption, optionValue } = item.item
        this.logSearchBoxSelection({
          selection_type: 'filter',
          filter_field: searchOption.field,
          // The value the search itself sends, so the two logs line up.
          filter_value: String(optionValue.value ?? optionValue[searchOption.filter_field] ?? optionValue.name)
        })
        this.searchSource = 'search_box'
        this.handleMultiOptionClick(searchOption, optionValue)
      }
      this.searchText = ''
    },

    // Fire and forget: analytics must never get in the way of the pick.
    logSearchBoxSelection: function (selection) {
      searchClient
        .logSearchBoxSelection({
          ...selection,
          query: this.searchText.trim(),
          session: currentSearchSession()
        })
        .catch(() => {})
    },

    getSuggestionValue: function (item) {
      // Used for the value in the search box when selected with keyboard.
      return item.item.fieldOption
    },

    artistRouteTo: function (artist) {
      return {
        name: 'artist-detail',
        params: {
          slug: artist.slug,
          artistId: artist.pk
        }
      }
    },

    playlistRouteTo: function (playlist) {
      return {
        name: 'playlist-detail',
        params: {
          slug: playlist.slug,
          playlistId: playlist.pk
        }
      }
    },

    fetchSearchBoxAPIResults: function () {
      clearTimeout(this.timeout)
      this.artistSearchResults = []
      this.playlistSearchResults = []

      const searchTerm = this.searchText.trim()
      if (searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
        return
      }

      this.timeout = setTimeout(() => {
        searchClient.searchArtists({ q: searchTerm })
          .then(response => {
            this.artistSearchResults = [...response.data.results]
          })

        searchClient.searchPlaylists({ q: searchTerm })
          .then(response => {
            this.playlistSearchResults = [...response.data.results]
          })
      }, this.debounceMilliseconds)
    }
  },

  metaInfo () {
    let url = window.location.origin
    const path = this.$route.path
    if (path !== '/') {
      url += path
    }
    const title = 'Browse & License Real Music For Your Content'
    const description = 'Discover and license real music from emerging artists, bands and composers for TV, Film, Adverts, Games. Sign up today and find the right song for your content.'

    // Filtered variants (?genre=…, ?mood=…) return the same track set as the
    // matching /browse/:slug landing page; noindex prevents them competing
    // with the landing page for the same query. Bare /tracks stays indexable.
    const isFiltered = Object.keys(this.$route.query || {}).length > 0
    const meta = [
      { name: 'description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: '/img/sonata-meta.png' },
      { name: 'twitter:card', content: 'summary' }
    ]
    if (isFiltered) {
      meta.push({ name: 'robots', content: 'noindex, follow' })
    }

    return { title, meta }
  }
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";

// The filter panel is collapsed on phones and tablets until the "Filter" button opens it, and
// always shown as a sidebar from desktop width.
.filter-bar.filter-panel {
  display: none;
  &.is-open {
    display: block;
  }
  @media screen and (min-width: $desktop) {
    display: block;
  }
}

.filter-bar {
  .card-header {
    .card-header-title .title {
      margin: 6px 0 6px 0 !important;
    }
  }
}

// Points people who can't find the right keywords at AI search.
.ai-search-nudge {
  margin: -4px 0 16px 20px;
  font-size: 14px;
  color: $bluegrey3;
  a {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-left: 4px;
    font-weight: 500;
    color: $bluegrey1;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
  .sparkle-icon {
    width: 14px;
    height: 14px;
  }
}

#autosuggest__input {
  caret-color: $bluegrey5;
  color: $bluegrey0;
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
}
.autosuggest__results-container {
  @media only screen and (max-width: $tablet) {
    width: 100%;
  }

  position: relative;
  width: 320px;
  background-color: $white;
}
.autosuggest__results {
  background-color: $white;
  margin: 0;
  position: absolute;
  z-index: 25;
  width: 100%;
  border: 1px solid $bluegrey6;
  border-bottom-left-radius: $radius-large;
  border-bottom-right-radius: $radius-large;
  padding: 6px 0 0 0;
  overflow-y: auto;
  // max-height: 400px;
}
.autosuggest__results ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
  background-color: $white;
}
.autosuggest__results .autosuggest__results-item {
  cursor: pointer;
  background-color: $white;
  padding: 0 12px;
  height: 48px;
}
h4.autosuggest__section-title {
  padding-left: 12px;
  margin: 24px 0px 6px 0px !important;
}

.autosuggest__results > ul:first-child > li:first-child h4.autosuggest__section-title {
  margin-top: 12px !important;
}

.autosuggest__results > ul:last-child > li:last-child {
  margin-bottom: 12px !important;
}

.autosuggest__results .autosuggest__results-item img {
  margin-top: 8px;
  margin-right: 12px;
}

.autosuggest__results .autosuggest__results-item .item-text {
  padding: 12px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.autosuggest__results .autosuggest__results-item:active,
.autosuggest__results .autosuggest__results-item:hover,
.autosuggest__results .autosuggest__results-item:focus,
.autosuggest__results .autosuggest__results-item.autosuggest__results-item--highlighted {
  background-color: $bluegrey6;
  color: $bluegrey0;
}

@keyframes tagFade {
  from { background: rgb(255, 108, 108); }
  to { background: $bluegrey7; }
}

.tag-accent-fade {
  animation-name: tagFade;
  animation-duration: 1s;
}
</style>
